import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Package,
  AlertTriangle,
  ArrowLeft,
  GitCompare,
  GitBranch,
  GitCommit,
  Search,
  Download,
  AlertCircle,
  Boxes,
  ChevronDown,
  ExternalLink,
  Plus,
  Minus,
  ArrowUpRight,
} from 'lucide-react';
import toast from '../../design-system/toast';
import { Spinner } from '../../design-system';
import { useAuthenticatedApi } from '../../hooks/useAuthenticatedApi';
import { apiEndpoints } from '../../utils/api';
import type {
  Sbom,
  SbomAdvisory,
  SbomComponent,
  SbomComponentListResponse,
  SbomDiff,
} from '../../types/sbom';

type FilterState = {
  search: string;
  hasCveOnly: boolean;
  ecosystem: string;
};

type View = 'components' | 'drift';

type SeverityKey = 'critical' | 'high' | 'medium' | 'low' | 'unknown';

const SEVERITY_ORDER: SeverityKey[] = ['critical', 'high', 'medium', 'low', 'unknown'];

const SEVERITY_STYLES: Record<SeverityKey, string> = {
  critical: 'bg-semantic-error/10 text-semantic-error border-semantic-error/20',
  high: 'bg-orange-500/10 text-orange-500 border-orange-500/20',
  medium: 'bg-semantic-warning/10 text-semantic-warning border-semantic-warning/20',
  low: 'bg-blue-500/10 text-blue-500 border-blue-500/20',
  unknown: 'bg-ui-background-tertiary text-ui-text-tertiary border-ui-border-secondary',
};

const renderLicense = (component: SbomComponent): string => {
  if (!component.licenses.length) return 'Unknown';
  return component.licenses
    .map((l) => l.spdx_id || l.raw)
    .filter(Boolean)
    .join(', ');
};

const canonicalAdvisoryUrl = (id: string): string | null => {
  if (id.startsWith('GHSA-')) return `https://github.com/advisories/${id}`;
  if (id.startsWith('CVE-')) return `https://nvd.nist.gov/vuln/detail/${id}`;
  return null;
};

const advisorySourceLabel = (url: string): string => {
  try {
    const hostname = new URL(url).hostname.replace(/^www\./, '');
    if (hostname === 'github.com') return url.includes('/advisories/') ? 'GitHub Advisory' : 'GitHub';
    if (hostname === 'nvd.nist.gov') return 'NVD';
    return hostname;
  } catch {
    return 'Reference';
  }
};

const severityKey = (severity?: string | null): SeverityKey => {
  const value = severity?.toLowerCase();
  return value === 'critical' || value === 'high' || value === 'medium' || value === 'low'
    ? value
    : 'unknown';
};

const sortedAdvisoryIds = (component: SbomComponent): string[] => {
  const advisories = component.extra?.advisories || {};
  return [...component.cves].sort((a, b) => {
    const severityA = severityKey(advisories[a]?.severity);
    const severityB = severityKey(advisories[b]?.severity);
    const severityDelta = SEVERITY_ORDER.indexOf(severityA) - SEVERITY_ORDER.indexOf(severityB);
    return severityDelta || a.localeCompare(b);
  });
};

const advisoryReferences = (advisoryId: string, advisory?: SbomAdvisory): string[] => {
  const refs = [canonicalAdvisoryUrl(advisoryId), ...(advisory?.urls || [])].filter(Boolean) as string[];
  (advisory?.related_ids || []).forEach((relatedId) => {
    const relatedUrl = canonicalAdvisoryUrl(relatedId);
    if (relatedUrl) refs.push(relatedUrl);
  });
  return Array.from(new Set(refs));
};

const SbomDetailPage: React.FC = () => {
  const { applicationId, sbomId } = useParams<{ applicationId: string; sbomId: string }>();
  const navigate = useNavigate();
  const api = useAuthenticatedApi();

  const [sbom, setSbom] = useState<Sbom | null>(null);
  const [components, setComponents] = useState<SbomComponent[]>([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<FilterState>({ search: '', hasCveOnly: false, ecosystem: '' });
  const [view, setView] = useState<View>('components');
  const [expandedComponentId, setExpandedComponentId] = useState<string | null>(null);

  const loadSbom = useCallback(async () => {
    if (!sbomId) return;
    try {
      setIsLoading(true);
      setError(null);
      const sbomResponse = await api.get<Sbom>(apiEndpoints.sboms.get(sbomId));
      setSbom(sbomResponse);
      const componentsResponse = await api.get<SbomComponentListResponse>(
        apiEndpoints.sboms.components(sbomId, {
          nameContains: filters.search || undefined,
          hasCve: filters.hasCveOnly ? true : undefined,
          ecosystem: filters.ecosystem || undefined,
          limit: 200,
          skip: 0,
        }),
      );
      setComponents(componentsResponse.components);
      setTotal(componentsResponse.total);
      setExpandedComponentId(null);
    } catch (err: any) {
      setError(err?.message || 'Failed to load SBOM');
    } finally {
      setIsLoading(false);
    }
  }, [api, sbomId, filters.search, filters.hasCveOnly, filters.ecosystem]);

  useEffect(() => {
    loadSbom();
  }, [loadSbom]);

  const ecosystems = useMemo(() => {
    const set = new Set<string>();
    components.forEach((c) => c.ecosystem && set.add(c.ecosystem));
    return Array.from(set).sort();
  }, [components]);

  const handleDownload = async () => {
    if (!sbom) return;
    try {
      const blob = await api.getBlob(apiEndpoints.sboms.download(sbom.id));
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `sbom-${sbom.id}.cyclonedx.json`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (err: any) {
      toast.error(err?.message || 'Failed to download SBOM');
    }
  };

  if (isLoading && !sbom) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <Spinner size={40} className="mx-auto mb-4" />
          <p className="text-ui-text-secondary">Loading SBOM…</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-ui-background-primary">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
          <div className="p-4 bg-semantic-error/10 border border-semantic-error/20 rounded-xl">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-semantic-error flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-semibold text-semantic-error mb-1">Failed to load SBOM</p>
                <p className="text-sm text-semantic-error">{error}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!sbom) return null;

  const diff: SbomDiff | null = sbom.diff;

  return (
    <div className="min-h-screen bg-ui-background-primary">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        <button
          type="button"
          onClick={() => navigate(`/repositories/${applicationId}/sbom`)}
          className="flex items-center gap-2 text-sm text-ui-text-secondary hover:text-ui-text-primary mb-6 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to SBOMs
        </button>

        <div className="flex items-start justify-between flex-wrap gap-4 mb-6">
          <div className="flex items-start gap-3 sm:gap-4 min-w-0">
            <div className="w-12 h-12 bg-ui-background-secondary border border-ui-border-primary rounded-xl flex items-center justify-center flex-shrink-0">
              <Package className="w-6 h-6 text-brand-primary" />
            </div>
            <div className="min-w-0">
              <h1 className="text-2xl font-bold text-ui-text-primary font-mono break-all">
                {sbom.repository_full_name}
              </h1>
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1.5 text-sm text-ui-text-secondary">
                {sbom.branch && (
                  <span className="inline-flex items-center gap-1">
                    <GitBranch className="w-3.5 h-3.5" />
                    <span className="font-mono">{sbom.branch}</span>
                  </span>
                )}
                {sbom.git_ref && (
                  <span className="inline-flex items-center gap-1">
                    <GitCommit className="w-3.5 h-3.5" />
                    <span className="font-mono">{sbom.git_ref.slice(0, 8)}</span>
                  </span>
                )}
                <span>{new Date(sbom.created_at).toLocaleString()}</span>
              </div>
            </div>
          </div>
          <button
            type="button"
            onClick={handleDownload}
            className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-ui-text-secondary hover:text-ui-text-primary bg-ui-background-secondary border border-ui-border-primary rounded-lg transition-colors"
          >
            <Download className="w-4 h-4" />
            Download CycloneDX
          </button>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
          <StatCard label="Components" value={sbom.component_count.toLocaleString()} icon={Boxes} />
          <StatCard
            label="Format"
            value={sbom.format}
            sublabel={sbom.spec_version ? `spec ${sbom.spec_version}` : undefined}
          />
          <StatCard
            label="Vulnerabilities"
            value={Object.values(sbom.vulnerability_summary).reduce((a, b) => a + b, 0).toLocaleString()}
          />
          <StatCard
            label="Drift vs prev"
            value={
              diff
                ? `${diff.added.length + diff.removed.length + diff.upgraded.length}`
                : '—'
            }
            sublabel={diff ? `${diff.added.length} added · ${diff.removed.length} removed` : 'No prior SBOM'}
          />
        </div>

        <div className="inline-flex p-1 bg-ui-background-secondary border border-ui-border-primary rounded-lg mb-6">
          <button
            type="button"
            onClick={() => setView('components')}
            className={`px-4 py-1.5 text-sm font-medium rounded-md transition-colors ${
              view === 'components'
                ? 'bg-brand-primary text-black'
                : 'text-ui-text-secondary hover:text-ui-text-primary'
            }`}
          >
            Components
          </button>
          <button
            type="button"
            onClick={() => setView('drift')}
            disabled={!diff}
            title={diff ? 'Show drift vs previous SBOM' : 'No previous SBOM to diff against'}
            className={`px-4 py-1.5 text-sm font-medium rounded-md transition-colors flex items-center gap-1.5 ${
              view === 'drift'
                ? 'bg-brand-primary text-black'
                : 'text-ui-text-secondary hover:text-ui-text-primary'
            } ${!diff ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            <GitCompare className="w-3.5 h-3.5" />
            Drift
          </button>
        </div>

        {view === 'components' && (
          <div className="bg-ui-background-secondary border border-ui-border-primary rounded-xl overflow-hidden">
            <div className="p-4 border-b border-ui-border-primary flex flex-wrap items-center gap-3">
              <div className="relative flex-1 min-w-[200px] max-w-md">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-ui-text-tertiary pointer-events-none" />
                <input
                  type="text"
                  placeholder="Filter by component name…"
                  value={filters.search}
                  onChange={(e) => setFilters((s) => ({ ...s, search: e.target.value }))}
                  className="w-full pl-9 pr-3 py-1.5 text-sm bg-ui-background-tertiary border border-ui-border-secondary rounded-lg text-ui-text-primary placeholder:text-ui-text-tertiary focus:outline-none focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary"
                />
              </div>
              <select
                value={filters.ecosystem}
                onChange={(e) => setFilters((s) => ({ ...s, ecosystem: e.target.value }))}
                className="px-3 py-1.5 text-sm bg-ui-background-tertiary border border-ui-border-secondary rounded-lg text-ui-text-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary"
              >
                <option value="">All ecosystems</option>
                {ecosystems.map((eco) => (
                  <option key={eco} value={eco}>
                    {eco}
                  </option>
                ))}
              </select>
              <label className="inline-flex items-center gap-2 px-3 py-1.5 text-sm text-ui-text-secondary bg-ui-background-tertiary border border-ui-border-secondary rounded-lg cursor-pointer hover:text-ui-text-primary">
                <input
                  type="checkbox"
                  checked={filters.hasCveOnly}
                  onChange={(e) => setFilters((s) => ({ ...s, hasCveOnly: e.target.checked }))}
                  className="rounded accent-brand-primary"
                />
                Has vulnerabilities
              </label>
              <span className="text-xs text-ui-text-tertiary ml-auto">
                {components.length} of {total.toLocaleString()}
              </span>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-ui-border-primary">
                    <th className="text-left text-xs font-medium text-ui-text-secondary uppercase tracking-wider py-3 px-4">
                      Name
                    </th>
                    <th className="text-left text-xs font-medium text-ui-text-secondary uppercase tracking-wider py-3 px-4">
                      Version
                    </th>
                    <th className="text-left text-xs font-medium text-ui-text-secondary uppercase tracking-wider py-3 px-4">
                      Ecosystem
                    </th>
                    <th className="text-left text-xs font-medium text-ui-text-secondary uppercase tracking-wider py-3 px-4">
                      License
                    </th>
                    <th className="text-left text-xs font-medium text-ui-text-secondary uppercase tracking-wider py-3 px-4">
                      Advisories
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-ui-border-primary">
                  {components.map((c) => {
                    const isExpanded = expandedComponentId === c.id;
                    return (
                      <React.Fragment key={c.id}>
                        <tr className="hover:bg-ui-background-tertiary transition-colors">
                          <td className="py-3 px-4">
                            <span className="text-sm font-medium text-ui-text-primary font-mono break-all">
                              {c.name}
                            </span>
                          </td>
                          <td className="py-3 px-4">
                            <span className="text-sm text-ui-text-secondary font-mono">{c.version || '—'}</span>
                          </td>
                          <td className="py-3 px-4">
                            {c.ecosystem ? (
                              <span className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium bg-ui-background-tertiary text-ui-text-secondary border border-ui-border-secondary">
                                {c.ecosystem}
                              </span>
                            ) : (
                              <span className="text-sm text-ui-text-tertiary">—</span>
                            )}
                          </td>
                          <td className="py-3 px-4">
                            <div className="flex items-center gap-1.5">
                              <span className="text-sm text-ui-text-secondary">{renderLicense(c)}</span>
                              <span
                                className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] uppercase tracking-wider font-medium text-ui-text-tertiary bg-ui-background-tertiary border border-ui-border-secondary"
                                title={`Evidence: ${c.license_evidence}`}
                              >
                                {c.license_evidence}
                              </span>
                            </div>
                          </td>
                          <td className="py-3 px-4">
                            {c.cves.length ? (
                              <button
                                type="button"
                                aria-expanded={isExpanded}
                                aria-label={`${isExpanded ? 'Hide' : 'Show'} ${c.cves.length} advisories for ${c.name}`}
                                title="Unique advisories matched for this component"
                                onClick={() => setExpandedComponentId(isExpanded ? null : c.id)}
                                className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium border transition-colors active:scale-[0.98] ${
                                  isExpanded
                                    ? 'bg-orange-500/15 text-orange-400 border-orange-500/30'
                                    : 'bg-orange-500/10 text-orange-500 border-orange-500/20 hover:bg-orange-500/15 hover:border-orange-500/30'
                                }`}
                              >
                                <AlertTriangle className="w-3.5 h-3.5" />
                                <span className="font-mono">{c.cves.length}</span>
                                <ChevronDown className={`w-3.5 h-3.5 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                              </button>
                            ) : (
                              <span className="text-sm text-ui-text-tertiary">—</span>
                            )}
                          </td>
                        </tr>
                        {isExpanded && (
                          <tr>
                            <td colSpan={5} className="bg-ui-background-tertiary/60 px-4 py-4">
                              <AdvisoryDrilldown component={c} />
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                  {components.length === 0 && (
                    <tr>
                      <td colSpan={5} className="py-12 text-center">
                        <div className="flex flex-col items-center gap-2 text-ui-text-tertiary">
                          <Search className="w-6 h-6" />
                          <p className="text-sm">No components match the filters.</p>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {view === 'drift' && diff && (
          <div className="space-y-4">
            <DriftSection
              title="Added"
              icon={Plus}
              purls={diff.added}
              accentBg="bg-semantic-success/10"
              accentText="text-semantic-success"
              accentBorder="border-semantic-success/20"
            />
            <DriftSection
              title="Removed"
              icon={Minus}
              purls={diff.removed}
              accentBg="bg-semantic-error/10"
              accentText="text-semantic-error"
              accentBorder="border-semantic-error/20"
            />
            <UpgradedSection upgraded={diff.upgraded} />
          </div>
        )}
      </div>
    </div>
  );
};

const StatCard: React.FC<{
  label: string;
  value: string;
  sublabel?: string;
  icon?: React.ComponentType<{ className?: string }>;
}> = ({ label, value, sublabel, icon: Icon }) => (
  <div className="bg-ui-background-secondary border border-ui-border-primary rounded-xl p-4">
    <div className="flex items-center gap-1.5">
      {Icon && <Icon className="w-3.5 h-3.5 text-ui-text-tertiary" />}
      <div className="text-xs font-medium text-ui-text-tertiary uppercase tracking-wider">{label}</div>
    </div>
    <div className="text-2xl font-bold text-ui-text-primary mt-1 truncate">{value}</div>
    {sublabel && <div className="text-xs text-ui-text-tertiary mt-0.5 truncate">{sublabel}</div>}
  </div>
);

const AdvisoryDrilldown: React.FC<{ component: SbomComponent }> = ({ component }) => {
  const advisories = component.extra?.advisories || {};
  const advisoryIds = sortedAdvisoryIds(component);

  return (
    <div className="rounded-lg border border-ui-border-primary bg-ui-background-secondary overflow-hidden">
      <div className="px-4 py-3 border-b border-ui-border-primary flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
        <div>
          <p className="text-sm font-semibold text-ui-text-primary">Matched advisories</p>
          <p className="text-xs text-ui-text-tertiary">
            Unique advisories matched for {component.name}{component.version ? ` ${component.version}` : ''}.
          </p>
        </div>
        <SeveritySummary severities={component.vuln_severities} />
      </div>
      <div className="divide-y divide-ui-border-primary">
        {advisoryIds.map((advisoryId) => {
          const advisory = advisories[advisoryId];
          return (
            <AdvisoryRow key={advisoryId} advisoryId={advisoryId} advisory={advisory} />
          );
        })}
      </div>
    </div>
  );
};

const AdvisoryRow: React.FC<{ advisoryId: string; advisory?: SbomAdvisory }> = ({ advisoryId, advisory }) => {
  const severity = severityKey(advisory?.severity);
  const references = advisoryReferences(advisoryId, advisory);
  const relatedIds = (advisory?.related_ids || []).filter((id) => id !== advisoryId);
  const fixedVersions = advisory?.fixed_versions?.filter(Boolean) || [];

  return (
    <div className="px-4 py-3">
      <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span className={`inline-flex px-2 py-0.5 rounded-md border text-[11px] font-semibold uppercase ${SEVERITY_STYLES[severity]}`}>
              {severity}
            </span>
            <span className="font-mono text-sm font-semibold text-ui-text-primary break-all">{advisoryId}</span>
            {relatedIds.map((id) => (
              <span key={id} className="font-mono text-xs text-ui-text-tertiary break-all">
                {id}
              </span>
            ))}
          </div>
          <p className="mt-2 text-sm text-ui-text-secondary leading-relaxed">
            {advisory?.description || 'No advisory description was included in this SBOM scan.'}
          </p>
          {fixedVersions.length > 0 && (
            <p className="mt-2 text-xs font-medium text-ui-text-primary">
              Fix: <span className="font-mono">{fixedVersions.slice(0, 3).join(', ')}</span>
            </p>
          )}
        </div>
        <div className="flex flex-wrap lg:justify-end gap-2 lg:max-w-sm">
          {references.slice(0, 4).map((url) => (
            <a
              key={url}
              href={url}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 px-2 py-1 rounded-md border border-ui-border-secondary bg-ui-background-tertiary text-xs font-medium text-ui-text-secondary hover:text-ui-text-primary transition-colors"
            >
              {advisorySourceLabel(url)}
              <ExternalLink className="w-3 h-3" />
            </a>
          ))}
        </div>
      </div>
    </div>
  );
};

const SeveritySummary: React.FC<{ severities: Record<string, number> }> = ({ severities }) => {
  const entries = SEVERITY_ORDER
    .filter((severity) => severity !== 'unknown')
    .map((severity) => [severity, severities[severity] || 0] as const)
    .filter(([, count]) => count > 0);

  if (!entries.length) return null;

  return (
    <div className="flex flex-wrap gap-1.5">
      {entries.map(([severity, count]) => (
        <span
          key={severity}
          className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md border text-[11px] font-medium ${SEVERITY_STYLES[severity]}`}
        >
          <span className="font-mono">{count}</span>
          {severity}
        </span>
      ))}
    </div>
  );
};

const DriftSection: React.FC<{
  title: string;
  icon: React.ComponentType<{ className?: string }>;
  purls: string[];
  accentBg: string;
  accentText: string;
  accentBorder: string;
}> = ({ title, icon: Icon, purls, accentBg, accentText, accentBorder }) => (
  <div className="bg-ui-background-secondary border border-ui-border-primary rounded-xl overflow-hidden">
    <div className="px-4 py-3 border-b border-ui-border-primary flex items-center gap-2">
      <span
        className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium border ${accentBg} ${accentText} ${accentBorder}`}
      >
        <Icon className="w-3.5 h-3.5" />
        {title}
      </span>
      <span className="text-sm text-ui-text-secondary">
        {purls.length} {purls.length === 1 ? 'component' : 'components'}
      </span>
    </div>
    {purls.length === 0 ? (
      <div className="px-4 py-6 text-sm text-ui-text-tertiary text-center">No changes.</div>
    ) : (
      <ul className="divide-y divide-ui-border-primary">
        {purls.map((p) => (
          <li key={p} className="px-4 py-2 text-sm font-mono text-ui-text-secondary break-all">
            {p}
          </li>
        ))}
      </ul>
    )}
  </div>
);

const UpgradedSection: React.FC<{ upgraded: SbomDiff['upgraded'] }> = ({ upgraded }) => (
  <div className="bg-ui-background-secondary border border-ui-border-primary rounded-xl overflow-hidden">
    <div className="px-4 py-3 border-b border-ui-border-primary flex items-center gap-2">
      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium border bg-semantic-info/10 text-semantic-info border-semantic-info/20">
        <ArrowUpRight className="w-3.5 h-3.5" />
        Upgraded
      </span>
      <span className="text-sm text-ui-text-secondary">
        {upgraded.length} {upgraded.length === 1 ? 'component' : 'components'}
      </span>
    </div>
    {upgraded.length === 0 ? (
      <div className="px-4 py-6 text-sm text-ui-text-tertiary text-center">No upgrades.</div>
    ) : (
      <ul className="divide-y divide-ui-border-primary">
        {upgraded.map((u) => (
          <li key={`${u.purl_old}->${u.purl_new}`} className="px-4 py-2 text-sm font-mono break-all">
            <span className="text-ui-text-tertiary">{u.purl_old}</span>
            <span className="mx-2 text-ui-text-tertiary">→</span>
            <span className="text-ui-text-primary">{u.purl_new}</span>
          </li>
        ))}
      </ul>
    )}
  </div>
);

export default SbomDetailPage;
